import { useFormik } from "formik";
import * as Yup from "yup";
import './signature.css';
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getAgentId, getSingleUser } from "../api/Individual";
import { failed, success } from "../common/Toastify";
import TextInput from "../common/textfield/TextInput";
import { Button } from "../common/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { decryptData, encryptData } from "../EncryptDecrypt";
import InputFile from "../common/textfield/InputFile";
import { useNavigate } from "react-router-dom";
import { editUserProfile } from "../api/Profile";
import CircularProgress from "@mui/material/CircularProgress";
import { FormikObj } from "./Constants";
import {
  splitContactNumber,
  createContactNumber,
  splitHomeContactNumber,
} from "../../utils/phoneNumberSupport";
import { handleFileUpload } from "../../utils/ImageToBase64";
import { getPractitionerSignatureSource, getProfilePhotoSource } from "../../utils/signatureSupport";
import ReactDatePicker from "../common/textfield/ReactDatePicker";
import SelectFiled from "../common/textfield/SelectFiled";
import SignatureCapture from "./SignatureCapture";
import { countryList, caregions, usregions } from "../../utils/constants";
import MultiSelect from "../common/textfield/MultiSelect";
import moment from "moment";
import { getOrganization, getOrganizationSettings } from "../api/Organization";
import CountrySelect from "../common/textfield/CountrySelect";
import RegionSelect from "../common/textfield/RegionSelect";
import { validation, conditionalValidation } from "../../utils/constants";

const UserProfile = () => {
  const navigate = useNavigate();
  const id = useSelector((state) => state?.auth?.user?.["custom:unique_id"]);
  const practitionerId = useSelector((state) => state?.auth?.user?.["custom:practitioner_id"]);
  const [btnLoading, setBtnLoading] = useState(false);
  const [file, setFile] = useState();
  const userData = useRef();
  const [inputValue, setInputValue] = useState("");
  const [designationOptions, setDesignationOptions] = useState([
    { value: "nurse", name: "Nurse" },
    { value: "nursePractitioner", name: "Nurse Practitioner" },
    { value: "physician", name: "Physician" },
  ]);
  const [signatureImage, setSignatureImage] = useState("");
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);

  const [sopRegionOptions, setSopRegionOptions] = useState([]);
  const [defaultSoPCountries, setDefaultSoPCountries] = useState([]);

  const userRoles = useSelector((state) => state?.auth?.user?.["cognito:groups"]);
  const practitioner = userRoles?.includes("Practitioner");
  const permissions = useSelector((state) => state?.auth?.user?.permissions);
  const hasFullAccess = permissions?.includes("practitioner-create") || userRoles?.includes("Super-Admin");
  const hasLimitedAccess = permissions?.includes("practitioner-edit");
  const adminOrgs = useSelector((state) => state?.auth?.user?.organizations);
  const isCTIEnabledRef = useRef(false);

  const [enforceDOB, setEnforceDOB] = useState(false);
  const [enforceGender, setEnforceGender] = useState(false);
  const [enforceAddress, setEnforceAddress] = useState(false);
  const [enforceCountry, setEnforceCountry] = useState(false);
  const [enforceProvince, setEnforceProvince] = useState(false);
  const [enforceCity, setEnforceCity] = useState(false);
  const [enforcePostal, setEnforcePostal] = useState(false);

  function getImageExtension(base64Data) {
    const base64String = (base64Data || "").replace(
      /^data:image\/(png|jpeg|jpg|gif);base64,/,
      ""
    );
    if (!base64String) return "unknown";
    const byteCharacters = atob(base64String);
    const byteArray = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++)
      byteArray[i] = byteCharacters.charCodeAt(i);
    if (byteArray[0] === 0x89 && byteArray[1] === 0x50) return "png";
    if (byteArray[0] === 0xff && byteArray[1] === 0xd8) return "jpeg";
    if (byteArray[0] === 0x47 && byteArray[1] === 0x49) return "gif";
    if (byteArray[0] === 0x42 && byteArray[1] === 0x4d) return "bmp";
    return "unknown";
  }

  useEffect(() => {
    (async () => {
      setBtnLoading(true);
      try {
        const orgId = adminOrgs?.[0]?.id;

        const org = await getOrganization(orgId);
        isCTIEnabledRef.current = org?.data?.ctiSettings?.enabled || false;

        // Automatically fetch agent ID if CTI is enabled
        if (isCTIEnabledRef.current && practitionerId) {
          getAgentId(practitionerId).then((agentRes) => {
            if (agentRes?.data) {
              formik.setFieldValue("agentId", agentRes?.data);
            } else {
              formik.setFieldValue("agentId", "");
            }
          }).catch(() => {
            formik.setFieldValue("agentId", "");
          });
        }

        const orgSettings = await getOrganizationSettings(orgId);
        const p = orgSettings?.data?.practitioner || {};
        setEnforceDOB(!!p.dob);
        setEnforceGender(!!p.gender);
        setEnforceAddress(!!p.address);
        setEnforceCountry(!!p.country);
        setEnforceProvince(!!p.province);
        setEnforceCity(!!p.city);
        setEnforcePostal(!!p.postalCode);

        const res = await getSingleUser({
          id,
          type: "Practitioner",
          userOrgId: orgId,
        });
        userData.current = res?.data;

        const first = decryptData(res?.data?.name?.[0]?.given?.[0] || "");
        const last = decryptData(res?.data?.name?.[0]?.family || "");
        const birth = res?.data?.birthDate
          ? decryptData(moment(res?.data?.birthDate).format("MM-DD-YYYY")) || ""
          : "";

        const wPhone = splitContactNumber(
          decryptData(
            res?.data?.telecom?.find((ele) => ele?.system === "phone")?.value ||
              ""
          )
        );
        const hPhone = splitHomeContactNumber(
          decryptData(
            res?.data?.telecom?.find((ele) => ele?.use === "home")?.value || ""
          )
        ).home_phone_number.replace(/^\+1/, "");

        formik.setFieldValue("first_name", first);
        formik.setFieldValue("last_name", last);
        formik.setFieldValue("date_of_birth", birth);
        formik.setFieldValue("phone_number", wPhone.phone_number);
        formik.setFieldValue("ext", wPhone.ext);
        formik.setFieldValue(
          "email",
          res?.data?.telecom?.find((ele) => ele?.system == "email")?.value || ""
        );

        formik.setFieldValue(
          "address",
          decryptData(res?.data?.address?.[0]?.line?.[0] || "")
        );
        formik.setFieldValue(
          "postalCode",
          decryptData(res?.data?.address?.[0]?.postalCode || "")
        );
        setInputValue(decryptData(res?.data?.address?.[0]?.postalCode || ""));
        formik.setFieldValue("city", decryptData(res?.data?.address?.[0]?.city || ""));
        formik.setFieldValue("gender", decryptData(res?.data?.gender || ""));
        formik.setFieldValue("id", res?.data?.id || "");
        formik.setFieldValue("country", res?.data?.address?.[0]?.country || "");
        formik.setFieldValue("region", res?.data?.address?.[0]?.state || "");
        

        formik.setFieldValue(
          "designation",
          decryptData(res?.data?.designation || "")
        );
        formik.setFieldValue(
          "qualification",
          decryptData(res?.data?.qualification?.[0]?.code?.coding?.[0]?.text || "")
        );
        formik.setFieldValue(
          "license",
          decryptData(res?.data?.qualification?.[0]?.identifier?.[0]?.value || "")
        );
        formik.setFieldValue("home_phone_number", hPhone);

        const scopes = res?.data?.scopeOfPractice || [];
        formik.setFieldValue("jurisdiction", scopes);

        const sopCountries = Array.from(
          new Set(scopes.map((s) => (s.includes("-") ? s.split("-")[0] : null)).filter(Boolean))
        );
        setDefaultSoPCountries(sopCountries);

        let merged = [];
        if (sopCountries.includes("CA")) merged = merged.concat(caregions);
        if (sopCountries.includes("US")) merged = merged.concat(usregions);
        merged = Array.from(new Map(merged.map((r) => [r.value, r])).values());
        setSopRegionOptions(merged);

        const designation = res?.data?.designation;
        setDesignationOptions((prev) =>
          prev.map((opt) => ({ ...opt, selected: opt?.value === designation }))
        );
      } catch (err) {
        failed(err?.response?.data?.message || err?.response?.data?.error || err.message);
      } finally {
        setBtnLoading(false);
      }
    })();
  }, []);

  const ValidationSchema = Yup.object({
    first_name: conditionalValidation(
        Yup.string()
        .max(50, "Must be 50 characters or less")
        .matches(/^\s*\S[\s\S]*$/, "Only blankspaces is not valid."),
        {
          validation,
          label: "First Name",
          mandatory: true
        }),
    last_name: conditionalValidation(
        Yup.string()
        .max(50, "Must be 50 characters or less")
        .matches(/^\s*\S[\s\S]*$/, "Only blankspaces is not valid."),
        {
          validation,
          label: "Last Name",
          mandatory: true
        }),
    qualification: conditionalValidation(
      Yup.string()
      .max(50, "Must be 50 characters or less")
      .matches(/^\s*\S[\s\S]*$/, "Only blankspaces is not valid."),
      {
        validation,
        label:"Qualifications"
      }),
    license: conditionalValidation(
      Yup.string()
      .max(20, "Must be 20 characters or less")
      .matches(/^\s*\S[\s\S]*$/, "Only blankspaces is not valid."),
      {
        validation,
        label: "License"
      }),
    date_of_birth: conditionalValidation(
            Yup.string()
              .nullable()
              .test(
                "valid-format",
                "Invalid format. Use MMM-DD-YYYY (JAN-01-2024).",
                function (value) {
                  if (value) { 
                    const isValid1 = moment(value, "MM/DD/YYYY", true).isValid();
                    const isValid2 = moment(value, "MM-DD-YYYY", true).isValid();
                    if (!isValid1 && !isValid2) return false; 
                    const dob = isValid1 ? moment(value, "MM/DD/YYYY") : moment(value, "MM-DD-YYYY"); 
                    
                    if (dob.isAfter(moment())) {
                      return this.createError({
                        message: "Date of birth cannot be in the future.",
                      });
                    }
                    let minDate = moment().subtract(125, "years");
                    if (dob.isBefore(minDate)) {
                      return this.createError({
                        message: "Date of Birth cannot be more than 125 years ago.",
                      });
                    } 
                  }
                  return true;
                }
              ),
              {
                validation,
                mandatory: enforceDOB,
                label: "Date of Birth"
              }), 
    phone_number: conditionalValidation(
              Yup.string()
                .matches(
                  /^(?:\+?1[-. ]?)?\(?\d{3}\)?[-. ]?\d{3}[-. ]?\d{4}$/,
                  "Enter Valid Phone Number."
                )
                .test(
                  "is-valid-or-empty-placeholder",
                  "Enter Valid Phone Number.",
                  (value) => {
                    if (value && value.trim() === "(___) ___-____") return true; // allow placeholder
                      return /^(?:\+?1[-. ]?)?\(?\d{3}\)?[-. ]?\d{3}[-. ]?\d{4}$/.test(value);
                    }
                ),
              {
                validation,
                label: "Phone Number"}),
      home_phone_number: conditionalValidation(
          Yup.string().matches(
            /^(?:\+?1[-. ]?)?\(?[0-9]\d{2}\)?[-. ]?\d{3}[-. ]?\d{4}$/,
            "Enter Valid Home Phone Number."
          ),
          { 
            validation,
            label: "Home Phone Number" 
          }
      ),
      ext: conditionalValidation(
            Yup.string().matches(/^\d{0,4}$/, "Must be 4 digits or less."),
            { validation }
          ),

      address: conditionalValidation(
          Yup.string()
          .matches(/^\s*\S[\s\S]*$/, "Only blankspaces is not valid."),
          {
            validation,
            mandatory: enforceAddress,
            label: "Address"}),
    
      city: conditionalValidation(
          Yup.string()
          .matches(/^\s*\S[\s\S]*$/, "Only blankspaces is not valid."),
          {
            validation,
            mandatory: enforceCity,
            label: "City"}),

      country: conditionalValidation(
        Yup.string()
          .matches(/^\s*\S[\s\S]*$/, "Only blankspaces is not valid."),
          {
            validation,
            mandatory: enforceCountry,
            label: "Country"}),

      region: conditionalValidation(
        Yup.string()
          .matches(/^\s*\S[\s\S]*$/, "Only blankspaces is not valid."),
          {
            validation,
            mandatory: enforceProvince}),

      postalCode: Yup.string().when(["type", "country"], (type, country, schema) =>
          conditionalValidation(
            country === "US"
              ? Yup.number()
                .typeError("Zip code must be a number")
              : Yup.string()
                .matches(
                  /^[A-Za-z]\d[A-Za-z] [0-9][A-Za-z]\d$/,
                  "Postal code must be in A1A 1A1 format."
                ),
              {
                validation,
                mandatory: enforcePostal,
                label: (country === "US")? "Zip code":"Postal code"})
      ),

    email: conditionalValidation(
      Yup.string().email("Invalid email address"),
      {
        validation,
        label: "Email Address",
        mandatory: true
      }),
    agentId: conditionalValidation(
      Yup.string().matches(/^\S+$/,"Agent Id cannot contain any whitespace"),
      {
        validation,
        label: "Agent Id"
      }),
    // SoP is decoupled; leave optional by default:
    countryList: Yup.array(),       // e.g. ["CA","US"]
    jurisdiction: Yup.array(),      // e.g. ["CA-ON","US-MI"]
  });

  const formik = useFormik({
    ...FormikObj(practitioner),
    validationSchema: ValidationSchema,
    onSubmit: async (values) => {
      values.first_name = values?.first_name?.trim();
      values.last_name = values?.last_name?.trim();
      values.address = values?.address?.trim();
      values.city = values?.city?.trim();
      values.country = values?.country?.trim();
      values.region = values?.region?.trim();
      values.postalCode = values?.postalCode?.trim();
      values.phone_number = values?.phone_number?.trim();
      values.home_phone_number = values?.home_phone_number?.trim();
      values.ext = values?.ext?.trim();
      values.email = values?.email?.trim();
      let base64ProfileImage = "";
      let base64LicenseImage = "";
      if (values?.profileImage) base64ProfileImage = await handleFileUpload(values?.profileImage);
      const profileExtension = values?.profileImage?.type?.split("/")[1];
      if (values?.file) base64LicenseImage = await handleFileUpload(values?.file);
      const fileExtension = values?.file?.type?.split("/")[1];

      const state = values?.region;
      const country = values?.country;
      const phoneNumber = createContactNumber(values);

      const encryptedData = {
        first_name: encryptData(values?.first_name),
        last_name: encryptData(values?.last_name),
        full_name: encryptData([values?.first_name, values?.last_name].join(" ")),
        address: encryptData(values?.address),
        date_of_birth: moment(values?.date_of_birth, ["MM-DD-YYYY", "MM/DD/YYYY"]).format("MM-DD-YYYY"),
        phone_number: encryptData(phoneNumber),
        postalCode: encryptData((values?.postalCode || "").toUpperCase()),
        city: encryptData(values?.city),
      };

      values.date_of_birth = moment(values?.date_of_birth, ["MM-DD-YYYY", "MM/DD/YYYY"]).format("MM-DD-YYYY");
      setBtnLoading(true);
      const signExtension = getImageExtension(signatureImage);

      editUserProfile(
        values,
        encryptedData,
        country,
        state,
        base64ProfileImage,
        profileExtension,
        base64LicenseImage,
        fileExtension,
        userData?.current,
        signatureImage,
        signExtension,
        adminOrgs?.[0]?.id
      )
        .then((res) => {
          if (res?.status === true) {
            navigate("/");
            success(res?.message);
          } else {
            failed(res?.error);
          }
        })
        .catch((res) => {
          failed(res?.response?.data?.message || res?.response?.data?.error || res?.message);
        })
        .finally(() => setBtnLoading(false));
    },
  });

  const handleAddressCountryChange = (newCountry) => {
    if (newCountry !== formik.values.country) {
      formik.setFieldValue("region", "");
      formik.setFieldValue("postalCode", "");
      formik.setFieldValue("city", "");
      setInputValue("");
    }
    formik.setFieldValue("country", newCountry);
  };

  const handlePostalCodeChange = (e) => {
    const { value } = e.target;
    if (formik?.values?.country === "US") {
      const digits = value.replace(/\D/g, "").slice(0, 5);
      setInputValue(digits);
      formik.setFieldValue("postalCode", digits);
      return;
    }
    const formattedValue = value.replace(/[^A-Za-z0-9]/g, "");
    const spacedValue = formattedValue.replace(/^([A-Za-z0-9]{3})([A-Za-z0-9]+)/, "$1 $2");
    setInputValue(spacedValue);
    formik.setFieldValue("postalCode", spacedValue);
  };

  const handleScopeCountryChange = (selectedCountries = []) => {
    formik.setFieldValue("countryList", selectedCountries);
    let merged = [];
    if (selectedCountries.includes("CA")) merged = merged.concat(caregions);
    if (selectedCountries.includes("US")) merged = merged.concat(usregions);
    const deduped = Array.from(new Map(merged.map((r) => [r.value, r])).values());
    setSopRegionOptions(deduped);

    const current = formik.values.jurisdiction || [];
    const valid = new Set(deduped.map((d) => d.value));
    const filtered = current.filter((j) => valid.has(j));
    formik.setFieldValue("jurisdiction", filtered);
  };

  const handleSignatureCapture = (imageData) => setSignatureImage(imageData);
  const openSignatureModal = () => setIsSignatureModalOpen(true);
  const closeSignatureModal = () => setIsSignatureModalOpen(false);


  const req = (flag) => ({ required: !!flag, hideRequired: !flag });

  return (
    <section className="common-listing">
      <div className="heading-wrap" />
      {btnLoading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
          <CircularProgress size={50} />
        </div>
      ) : (
        <div className="custom-card p-4">
          <form className="common-form border-fields" onSubmit={formik.handleSubmit}>
            <Row>
              <Col>
                <TextInput
                  disabled={!hasFullAccess}
                  style={{ textTransform: "capitalize" }}
                  keyField={"first_name"}
                  label={"First Name"}
                  formik={formik}
                  placeholder={"First Name"}
                  required
                />
              </Col>
              <Col>
                <TextInput
                  disabled={!hasFullAccess}
                  style={{ textTransform: "capitalize" }}
                  keyField={"last_name"}
                  label={"Last Name"}
                  formik={formik}
                  placeholder={"Last Name"}
                  required
                />
              </Col>
              <Col></Col>
            </Row>

            <Row>
              <Col>
                <SelectFiled
                  readOnly={!hasFullAccess}
                  keyField={"designation"}
                  label={"Designation"}
                  formik={formik}
                  options={designationOptions?.map((option) => ({
                    ...option,
                    selected: option?.value === formik?.values?.designation,
                  }))}
                />
              </Col>
              <Col>
                <TextInput
                  disabled={!hasFullAccess}
                  keyField={"qualification"}
                  label={"College"}
                  formik={formik}
                  placeholder={"College "}
                  required={false}
                  hideRequired={true}
                />
              </Col>
              <Col>
                <TextInput
                  disabled={!hasFullAccess}
                  keyField={"license"}
                  label={"License Number"}
                  formik={formik}
                  placeholder={"License Number"}
                  required={false}
                  hideRequired={true}
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <ReactDatePicker
                  readOnly={!hasFullAccess}
                  keyField={"date_of_birth"}
                  label={"Date of Birth"}
                  formik={formik}
                  placeholder={"Select Date"}
                  hasValue={formik?.values?.date_of_birth}
                  displayAge={practitioner ? true : false}
                  {...req(enforceDOB)}
                />
              </Col>
              <Col>
                <TextInput
                  disabled={!hasFullAccess}
                  keyField={"phone_number"}
                  type="phone"
                  label={"Work Phone Number"}
                  formik={formik}
                  placeholder={"Work Phone Number"}
                  required={false}
                  hideRequired={true}
                />
              </Col>
              <Col>
                <TextInput
                  disabled={!hasFullAccess}
                  keyField={"ext"}
                  type="ext"
                  label={"Ext."}
                  formik={formik}
                  placeholder={"Ext"}
                  required={false}
                  hideRequired={true}
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <TextInput
                  disabled={!hasFullAccess}
                  keyField={"home_phone_number"}
                  type="phone"
                  label={"Home Phone Number"}
                  formik={formik}
                  placeholder={"Home Phone Number"}
                  required={false}
                  hideRequired={true}
                />
              </Col>
              <Col>
                <TextInput keyField={"email"} label={"Email"} formik={formik} placeholder={"Email"} disabled />
              </Col>
              <Col />
            </Row>

            <Row>
              <Col>
                <TextInput
                  disabled={!hasFullAccess}
                  keyField={"address"}
                  label={"Address"}
                  formik={formik}
                  placeholder={"Address"}
                  {...req(enforceAddress)}
                />
              </Col>
              <Col>
                <TextInput
                  disabled={!hasFullAccess}
                  onChange={handlePostalCodeChange}
                  value={inputValue}
                  keyField={"postalCode"}
                  label={"Postal Code"}
                  formik={formik}
                  placeholder={"Postal Code"}
                  maxLength={formik?.values?.country === "US" ? "5" : "7"}
                  {...req(enforcePostal)}
                />
              </Col>
              <Col></Col>
            </Row>

            <Row>
              <Col>
                <CountrySelect
                  readOnly={!hasFullAccess}
                  setCountry={handleAddressCountryChange}
                  keyField={"country"}
                  label={"Country"}
                  formik={formik}
                  {...req(enforceCountry)}
                />
              </Col>
              <Col>
                <RegionSelect
                  disabled={!hasFullAccess}
                  keyField={"region"}
                  defaultOptionLabel={formik?.values?.country === "US" ? "Select State" : "Select Province"}
                  label={formik?.values?.country === "US" ? "State" : "Province"}
                  formik={formik}
                  {...req(enforceProvince)}
                />
              </Col>
              <Col></Col>
            </Row>

            <Row>
              <Col>
                <TextInput
                  disabled={!hasFullAccess}
                  keyField={"city"}
                  label={"City"}
                  formik={formik}
                  placeholder={"City"}
                  {...req(enforceCity)}
                />
              </Col>
              <Col></Col>
              <Col></Col>
            </Row>

            <Row style={{ borderTop: "1px solid rgba(0,0,0,0.14)", marginTop: 12, paddingTop: 12 }}>
              <Col xs lg="3">
                <MultiSelect
                  options={countryList}
                  keyField={"countryList"}
                  formik={formik}
                  isSelectAllEnable={true}
                  label={"Scope of Practice — Country"}
                  style={{ width: "100%" }}
                  bindMethod={handleScopeCountryChange}
                  defaultValue={defaultSoPCountries}
                />
              </Col>
              <Col>
                <MultiSelect
                  options={sopRegionOptions}
                  keyField={"jurisdiction"}
                  formik={formik}
                  isSelectAllEnable={true}
                  label={"Scope of Clinical Jurisdictions"}
                  defaultValue={formik.values.jurisdiction}
                />
              </Col>
            </Row>
            <Row  style={{ borderTop: "1px solid rgba(0,0,0,0.14)", marginTop: 12, paddingTop: 12 }}>
              {isCTIEnabledRef.current && (
                <Col>
                  <TextInput
                    disabled={true}
                    keyField={"agentId"}
                    label={"Agent ID"}
                    tooltip={"User identifier from telephony agent"}
                    formik={formik}
                    placeholder={"Agent ID"}
                  />
                </Col>
              )}
            </Row>

            <Row>
              {(hasFullAccess || hasLimitedAccess) && (
                <Col>
                  <InputFile
                    keyField={"profileImage"}
                    label={"Upload Profile Image"}
                    formik={formik}
                    placeholder={"Upload Profile Image"}
                    setFile={setFile}
                    file={file}
                    imageUrl={getProfilePhotoSource(userData?.current?.photo)}
                  />
                </Col>
              )}
              {(!hasFullAccess || !hasLimitedAccess) && (
                <Col>
                  <div className="field-wrap">
                    <div>
                      {getProfilePhotoSource(userData?.current?.photo) && (
                        <img
                          height={100}
                          width={200}
                          src={getProfilePhotoSource(userData?.current?.photo)}
                          alt="selected file"
                        />
                      )}
                    </div>
                  </div>
                </Col>
              )}
              <Col></Col>
              <Col></Col>
            </Row>

            <Row>
              <Col>
                  <img src={signatureImage || getPractitionerSignatureSource(userData?.current?.signatureURL)} alt="Signature" />
              </Col>
              <Col></Col>
              <Col></Col>
            </Row>

            <Row>
              {(hasFullAccess || hasLimitedAccess) && (
                <div>
                  <button className="btn-wrap" type="button" onClick={() => setIsSignatureModalOpen(true)}>
                    Click Here To Set Up Signature
                  </button>
                </div>
              )}

              {isSignatureModalOpen && (
                <div className="signature-modal">
                  <div className="signature-modal-content">
                    <span className="close" onClick={() => setIsSignatureModalOpen(false)}>
                      &times;
                    </span>
                    <SignatureCapture
                      onSignatureCapture={setSignatureImage}
                      closeSignatureModal={() => setIsSignatureModalOpen(false)}
                    />
                  </div>
                </div>
              )}
            </Row>

            <div className="btn-wrap">
              {(hasFullAccess || hasLimitedAccess) && (
                <Button type="submit" isLoading={btnLoading}>
                  Update Details
                </Button>
              )}
              <Button
                onClick={() => navigate("/app/dashboard")}
                variant="secondary"
                title="Cancel"
                style={{ marginLeft: "10px" }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
};

export default UserProfile;